import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import confetti from 'canvas-confetti';
import cn from 'classnames';
import styles from './CollabWorkspace.module.sass';
import CollabIntroVideo from '../CollabIntroVideo';
import ReviewShareModal from '../ReviewShareModal';
import ReviewShareCard from '../ReviewShareCard';
import ReviewHeroUpload, { EMPTY_HERO } from '../ReviewHeroUpload';
import { getAuthToken } from '../../utils/authHelpers';
import { uploadFile } from '../../utils/fileUpload';
import { getOptimizedAvatarUrl, getCloudinaryUrl } from '../../utils/cloudinary';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Briefcase,
  Calendar,
  Camera,
  Check,
  CheckCircle,
  ChevronDown,
  CircleCheck,
  Clock,
  Eye,
  FileText,
  FolderOpen,
  GripVertical,
  HelpCircle,
  History,
  Image,
  Link as LinkIcon,
  ListChecks,
  Lock,
  LockOpen,
  Pencil,
  Plus,
  Printer,
  Send,
  Star,
  StickyNote,
  Trash2,
  Upload,
  UserCheck,
  Video,
  X,
} from 'lucide-react';
import Tippy from '@tippyjs/react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { showToast } from '../Messaging/Toast';

/** Check whether an agreement item is one of the protected mandatory agreements */
const RISK_SUBSTRINGS = ['assuming all risks', 'Postaway is not responsible'];
const REQUIRED_TAG_AGREEMENT_TEXT = 'Agree to tag @postawaytravels in stories and reels produced from this collab';
const normalizeAgreementTitle = (value = '') => String(value).toLowerCase().replace(/\s+/g, ' ').trim();
const isRiskAcknowledgement = (item) =>
  RISK_SUBSTRINGS.some((s) => normalizeAgreementTitle(item?.title).includes(normalizeAgreementTitle(s)));
const isPostawayTagAgreement = (item) =>
  normalizeAgreementTitle(item?.title) === normalizeAgreementTitle(REQUIRED_TAG_AGREEMENT_TEXT);
const isMandatoryAgreement = (item) => isRiskAcknowledgement(item) || isPostawayTagAgreement(item);
const getAgreementSortRank = (item) => {
  if (isPostawayTagAgreement(item)) return 2;
  if (isRiskAcknowledgement(item)) return 1;
  return 0;
};

const toYMD = (v) => {
  if (v == null || v === '') return '';
  if (v instanceof Date) return isNaN(v.getTime()) ? '' : v.toISOString().slice(0, 10);
  const s = String(v).trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

const CHECKIN_OPTIONS = [
  { id: 'on_track', label: 'On track', Icon: Check, color: '#10b981' },
  { id: 'blocked', label: 'Blocked', Icon: AlertTriangle, color: '#ef4444' },
  { id: 'need_reschedule', label: 'Need to reschedule', Icon: Calendar, color: '#f59e0b' },
  { id: 'need_help', label: 'Need help', Icon: HelpCircle, color: '#6366f1' },
];

const STEPS = [
  { id: 'agreements', step: 1, label: 'Expectations + Deliverables', mobileLabel: 'Expectations', Icon: ListChecks },
  { id: 'media',      step: 2, label: 'Content Library',            mobileLabel: 'Content',      Icon: FolderOpen },
  { id: 'review',     step: 3, label: 'Review',                     mobileLabel: 'Review',       Icon: UserCheck },
];

function formatDurationSince(timestamp) {
  if (!timestamp) return null;
  const ms = Date.now() - new Date(timestamp).getTime();
  if (ms < 0) return null;
  const totalMinutes = Math.floor(ms / 60000);
  const totalHours = Math.floor(totalMinutes / 60);
  const totalDays = Math.floor(totalHours / 24);
  if (totalDays > 0) {
    const hrs = totalHours % 24;
    return `${totalDays}d ${hrs}h ago`;
  }
  if (totalHours > 0) {
    const mins = totalMinutes % 60;
    return `${totalHours}h ${mins}m ago`;
  }
  if (totalMinutes > 0) return `${totalMinutes}m ago`;
  return 'just now';
}

function CollabActivityTimer({ lastActivityAt }) {
  const [display, setDisplay] = useState(() => formatDurationSince(lastActivityAt));

  useEffect(() => {
    if (!lastActivityAt) return;
    setDisplay(formatDurationSince(lastActivityAt));
    const id = setInterval(() => setDisplay(formatDurationSince(lastActivityAt)), 60000);
    return () => clearInterval(id);
  }, [lastActivityAt]);

  if (!display) return null;

  const ms = Date.now() - new Date(lastActivityAt).getTime();
  const daysSince = ms / (1000 * 60 * 60 * 24);
  const isStale = daysSince >= 3;

  return (
    <span className={cn(styles.activityTimer, isStale && styles.activityTimerStale)} title="Time since last activity on this collaboration">
      <Clock size={13} />
      Last activity: {display}
    </span>
  );
}

const DEFAULT_AVATAR = '/images/content/avatar-1.jpg';

// Human-readable event labels
const EVENT_LABELS = {
  collab_request_created: 'Collab request created',
  collab_request_accepted: 'Collab request accepted',
  collab_request_declined: 'Collab request declined',
  collab_agreement_confirmed: 'Confirmed expectation',
  collab_agreement_unconfirmed: 'Unconfirmed expectation',
  collab_agreement_created: 'Added expectation',
  collab_agreement_updated: 'Updated expectation',
  collab_agreement_deleted: 'Deleted expectation',
  collab_condition_suggestion: 'Suggested change to expectation',
  collab_condition_suggestion_accepted: 'Accepted suggested change',
  collab_condition_suggestion_declined: 'Declined suggested change',
  collab_checklist_completed: 'Completed checklist item',
  collab_checklist_uncompleted: 'Uncompleted checklist item',
  collab_deliverable_created: 'Added deliverable',
  collab_deliverable_submitted: 'Submitted deliverable',
  collab_deliverable_approved: 'Approved deliverable',
  collab_deliverable_changes_requested: 'Requested changes on deliverable',
  collab_checkin_submitted: 'Checked in',
  collab_dates_updated: 'Updated approximate dates',
  collab_notes_updated: 'Updated general notes',
  collab_completed: 'Marked collaboration complete',
  collab_review_submitted: 'Submitted review',
  collab_target_updated: 'Changed attributed property',
  collab_agreement_reordered: 'Reordered expectations',
  collab_deliverable_reordered: 'Reordered deliverables',
};

/** Sortable wrapper for a draggable agreement row */
const SortableAgreementRow = ({ id, isLocked, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: isLocked });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
    position: 'relative',
    zIndex: isDragging ? 10 : undefined,
  };

  return children({ setNodeRef, style, attributes, listeners, isDragging });
};

/** Sortable wrapper for a draggable deliverable row */
const SortableDeliverableRow = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
    position: 'relative',
    zIndex: isDragging ? 10 : undefined,
  };

  return children({ setNodeRef, style, attributes, listeners, isDragging });
};

const CollabWorkspace = ({ collaborationId, channelId, onClose: _onClose }) => {
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI state
  const [activeTab, setActiveTab] = useState('agreements');
  const [showAddDeliverable, setShowAddDeliverable] = useState(false);
  const [_showCheckin, setShowCheckin] = useState(false);
  const [checkinNote, setCheckinNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [approxCollaborationDate, setApproxCollaborationDate] = useState('');
  const [approxPostingDate, setApproxPostingDate] = useState('');
  const [approxDateError, setApproxDateError] = useState('');
  const [savingApproxDates, setSavingApproxDates] = useState(false);
  const [approxDateSavedNotice, setApproxDateSavedNotice] = useState(false);
  const [datesLoadedForCollabId, setDatesLoadedForCollabId] = useState(null);

  // General notes state
  const [generalNotes, setGeneralNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSavedNotice, setNotesSavedNotice] = useState(false);
  const [notesError, setNotesError] = useState('');
  const [notesLoadedForCollabId, setNotesLoadedForCollabId] = useState(null);
  const notesSavedNoticeTimeoutRef = useRef(null);

  // Activity panel collapsed/expanded
  const [activityOpen, setActivityOpen] = useState(false);

  // Ref for the Mark Complete button (confetti origin)
  const completeBtnRef = useRef(null);

  // Refs for step tab buttons (keyboard navigation)
  const stepRefs = useRef([]);
  const approxDateSavedNoticeTimeoutRef = useRef(null);

  // Agreement state
  const [editingAgreementId, setEditingAgreementId] = useState(null);
  const [editingAgreementText, setEditingAgreementText] = useState('');
  const [suggestingId, setSuggestingId] = useState(null);
  const [suggestionText, setSuggestionText] = useState('');
  const [expandedNoteId, setExpandedNoteId] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');

  // Deliverables: request changes flow (host)
  const [_requestingChangesDeliverableId, setRequestingChangesDeliverableId] = useState(null);
  const [requestChangesText, setRequestChangesText] = useState('');
  const [_resubmittingDeliverableId, setResubmittingDeliverableId] = useState(null);
  const [resubmitNoteText, setResubmitNoteText] = useState('');
  const [resubmitUrlText, setResubmitUrlText] = useState('');
  const [_resubmitUploading, setResubmitUploading] = useState(false);
  const [_submittingDeliverableId, setSubmittingDeliverableId] = useState(null);
  const [submitUrlText, setSubmitUrlText] = useState('');
  const [submitNoteText, setSubmitNoteText] = useState('');
  const [_submitUploading, setSubmitUploading] = useState(false);
  
  // Review form state
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: '', text: '' });
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [editingReviewId, setEditingReviewId] = useState(null);
  const [shareReview, setShareReview] = useState(null);
  // Background + circle photos for the shareable review card:
  //   { url, localUrl, uploading, progress, error }
  const [reviewHero, setReviewHero] = useState(EMPTY_HERO);
  const [reviewCircle, setReviewCircle] = useState(EMPTY_HERO);
  // Debounced copy of reviewForm.text feeding the live share-card preview
  const [previewText, setPreviewText] = useState('');
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setPreviewText(reviewForm.text), 150);
    return () => clearTimeout(t);
  }, [reviewForm.text]);
  
  // Form state for adding deliverables
  const [newDeliverable, setNewDeliverable] = useState({
    kind: 'link',
    title: '',
    url: '',
    description: '',
  });
  const [uploadingFile, setUploadingFile] = useState(false);

  // Host property attribution
  const [hostProperties, setHostProperties] = useState([]);
  const [savingTarget, setSavingTarget] = useState(false);
  const [savingAll, setSavingAll] = useState(false);

  const apiBase = typeof window !== 'undefined' && window.location.hostname === 'localhost' 
    ? 'http://localhost:4000' 
    : '';

  // Current user's role
  const currentUserRole = workspace?.currentUser?.role;
  const isHost = currentUserRole === 'host';
  const isCreator = currentUserRole === 'creator';

  // Fetch workspace data
  const fetchWorkspace = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const params = new URLSearchParams();
      if (collaborationId) params.set('collaborationId', collaborationId);
      if (channelId) params.set('channelId', channelId);

      const response = await fetch(`${apiBase}/api/collab/workspace?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const data = await response.json();

      if (data.ok) {
        setWorkspace(data);
        setError(null);

        // Derive review-submitted state from server data — only for THIS collab
        if (data.existingReviews && data.currentUser) {
          const role = data.currentUser.role;
          const collabId = data.collaboration?.id;
          const myReviews = role === 'creator'
            ? (data.existingReviews.allCreatorReviews || [])
            : (data.existingReviews.allHostReviews || []);
          const hasReviewForThisCollab = myReviews.some(r => r.collaborationId === collabId);
          if (hasReviewForThisCollab) {
            setReviewSubmitted(true);
          }
        }
      } else {
        setError(data.error || 'Failed to load workspace');
      }
    } catch (err) {
      console.error('Failed to fetch workspace:', err);
      setError('Failed to load workspace');
    } finally {
      setLoading(false);
    }
  }, [collaborationId, channelId, apiBase]);

  useEffect(() => {
    fetchWorkspace();
  }, [fetchWorkspace]);

  useEffect(() => () => {
    if (approxDateSavedNoticeTimeoutRef.current) {
      clearTimeout(approxDateSavedNoticeTimeoutRef.current);
      approxDateSavedNoticeTimeoutRef.current = null;
    }
    if (notesSavedNoticeTimeoutRef.current) {
      clearTimeout(notesSavedNoticeTimeoutRef.current);
      notesSavedNoticeTimeoutRef.current = null;
    }
  }, []);

  // Keep approximate date inputs in sync with loaded collaboration data.
  useEffect(() => {
    const serverCollabId = workspace?.collaboration?.id;
    if (!serverCollabId) return;

    const serverCollabDate = toYMD(workspace?.collaboration?.startDate);
    const serverPostingDate = toYMD(workspace?.collaboration?.endDate);
    const isDifferentCollab = datesLoadedForCollabId !== serverCollabId;
    const hasUnsavedLocalChanges =
      (approxCollaborationDate || '') !== serverCollabDate ||
      (approxPostingDate || '') !== serverPostingDate;

    if (isDifferentCollab || !hasUnsavedLocalChanges) {
      setApproxCollaborationDate(serverCollabDate);
      setApproxPostingDate(serverPostingDate);
      setApproxDateError('');
      setDatesLoadedForCollabId(serverCollabId);
    }
  }, [
    workspace?.collaboration,
    workspace?.collaboration?.id,
    workspace?.collaboration?.startDate,
    workspace?.collaboration?.endDate,
    approxCollaborationDate,
    approxPostingDate,
    datesLoadedForCollabId,
  ]);

  // Reset the note input when switching collaborations.
  useEffect(() => {
    const serverCollabId = workspace?.collaboration?.id;
    if (!serverCollabId) return;

    if (notesLoadedForCollabId !== serverCollabId) {
      setGeneralNotes('');
      setNotesError('');
      setNotesLoadedForCollabId(serverCollabId);
    }
  }, [workspace?.collaboration?.id, notesLoadedForCollabId]);

  // Fetch host's properties so the target-property dropdown can be shown
  useEffect(() => {
    if (!isHost || !workspace?.currentUser?.email) return;
    const email = workspace.currentUser.email;
    (async () => {
      try {
        const res = await fetch(`${apiBase}/api/properties/list?email=${encodeURIComponent(email)}`);
        const data = await res.json();
        if (data.ok && Array.isArray(data.properties)) {
          setHostProperties(data.properties);
        }
      } catch (_) { /* non-critical */ }
    })();
  }, [isHost, workspace?.currentUser?.email, apiBase]);

  // Update the collaboration's attributed property
  const handleChangeTargetProperty = async (propertyId) => {
    const token = getAuthToken();
    if (!token || !workspace?.collaboration?.id) return;
    setSavingTarget(true);
    try {
      const response = await fetch(`${apiBase}/api/collab/update-target`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          collaborationId: workspace.collaboration.id,
          targetPropertyId: propertyId,
        }),
      });
      const data = await response.json();
      if (data.ok) {
        fetchWorkspace();
      }
    } catch (err) {
      console.error('Failed to update target property:', err);
    } finally {
      setSavingTarget(false);
    }
  };

  // Toggle agreement confirmation (role-specific)
  const handleToggleAgreement = async (itemId, confirmed) => {
    const token = getAuthToken();
    if (!token) return;

    try {
      await fetch(`${apiBase}/api/collab/checklist/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ itemId, confirmed }),
      });
      
      fetchWorkspace();
    } catch (err) {
      console.error('Failed to toggle agreement:', err);
    }
  };

  // Add new agreement - creates with placeholder text, then user can edit inline
  const handleAddAgreement = async () => {
    const token = getAuthToken();
    if (!token) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`${apiBase}/api/collab/checklist/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          collaborationId: workspace.collaboration.id,
          title: 'New expectation - click to edit',
        }),
      });
      
      const data = await response.json();
      await fetchWorkspace();
      
      // Auto-focus the new item for editing
      if (data.ok && data.item?.id) {
        setTimeout(() => {
          setEditingAgreementId(data.item.id);
          setEditingAgreementText('');
        }, 100);
      }
    } catch (err) {
      console.error('Failed to add agreement:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Start editing agreement
  const _handleStartEditAgreement = (item) => {
    setEditingAgreementId(item.id);
    setEditingAgreementText(item.title);
  };

  // Save edited agreement
  const handleSaveEditAgreement = async (itemId) => {
    if (!editingAgreementText.trim()) return;

    const token = getAuthToken();
    if (!token) return;

    try {
      await fetch(`${apiBase}/api/collab/checklist/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          itemId,
          title: editingAgreementText.trim(),
        }),
      });
      
      setEditingAgreementId(null);
      setEditingAgreementText('');
      fetchWorkspace();
    } catch (err) {
      console.error('Failed to update agreement:', err);
    }
  };

  // Cancel editing
  const handleCancelEditAgreement = () => {
    setEditingAgreementId(null);
    setEditingAgreementText('');
  };

  // Toggle note expansion
  const handleToggleNote = (itemId) => {
    if (expandedNoteId === itemId) {
      setExpandedNoteId(null);
      setEditingNoteId(null);
    } else {
      setExpandedNoteId(itemId);
    }
  };

  // Start editing note
  const handleStartEditNote = (item) => {
    setEditingNoteId(item.id);
    setEditingNoteText(item.description || '');
  };

  // Save note
  const handleSaveNote = async (itemId) => {
    const token = getAuthToken();
    if (!token) return;

    try {
      // Find the current item to get its title
      const item = agreements.find(a => a.id === itemId);
      if (!item) return;

      await fetch(`${apiBase}/api/collab/checklist/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          itemId,
          title: item.title,
          description: editingNoteText.trim(),
        }),
      });
      
      setEditingNoteId(null);
      setEditingNoteText('');
      fetchWorkspace();
    } catch (err) {
      console.error('Failed to save note:', err);
    }
  };

  // Suggest edit to agreement
  const handleStartSuggest = (item) => {
    // Block suggestions on protected mandatory agreement items
    if (isMandatoryAgreement(item)) return;

    setSuggestingId(item.id);
    setSuggestionText(item.title);
  };

  const handleCancelSuggest = () => {
    setSuggestingId(null);
    setSuggestionText('');
  };

  const handleSubmitSuggestion = async (itemId) => {
    if (!suggestionText.trim()) return;

    const token = getAuthToken();
    if (!token) return;

    try {
      await fetch(`${apiBase}/api/collab/checklist/suggest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          itemId,
          suggestedText: suggestionText.trim(),
        }),
      });
      
      setSuggestingId(null);
      setSuggestionText('');
      fetchWorkspace();
    } catch (err) {
      console.error('Failed to suggest edit:', err);
    }
  };

  const handleAcceptSuggestion = async (itemId) => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await fetch(`${apiBase}/api/collab/checklist/accept-suggestion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ itemId }),
      });
      
      const data = await response.json();
      if (!data.ok && data.error === 'cannot_accept_own_suggestion') {
        alert(data.message);
        return;
      }
      
      fetchWorkspace();
    } catch (err) {
      console.error('Failed to accept suggestion:', err);
    }
  };

  const handleDeclineSuggestion = async (itemId) => {
    const token = getAuthToken();
    if (!token) return;

    try {
      await fetch(`${apiBase}/api/collab/checklist/decline-suggestion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ itemId }),
      });
      
      fetchWorkspace();
    } catch (err) {
      console.error('Failed to decline suggestion:', err);
    }
  };

  // Delete agreement
  const handleDeleteAgreement = async (itemId, title) => {
    // Block deletion of protected mandatory agreement items
    const item = agreements.find(a => a.id === itemId);
    if (item && isMandatoryAgreement(item)) return;

    if (!window.confirm(`Delete this expectation?\n\n"${title}"`)) return;

    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await fetch(`${apiBase}/api/collab/checklist/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ itemId }),
      });
      
      const data = await response.json();
      if (!data.ok && data.error === 'item_locked') {
        alert('Cannot delete an expectation that both parties have confirmed.');
        return;
      }
      
      fetchWorkspace();
    } catch (err) {
      console.error('Failed to delete agreement:', err);
    }
  };

  // Reorder agreements (autosave on drop)
  const handleReorderAgreements = useCallback(async (newOrderIds) => {
    const token = getAuthToken();
    if (!token || !workspace?.collaboration?.id) return;
    try {
      const res = await fetch(`${apiBase}/api/collab/checklist/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          collaborationId: workspace.collaboration.id,
          itemIds: newOrderIds,
        }),
      });
      const data = await res.json();
      if (!data.ok) {
        console.error('Reorder failed:', data.error);
        fetchWorkspace();
      }
    } catch (err) {
      console.error('Failed to reorder agreements:', err);
      fetchWorkspace();
    }
  }, [workspace?.collaboration?.id, apiBase, fetchWorkspace]);

  // Reorder deliverables (autosave on drop)
  const handleReorderDeliverables = useCallback(async (newOrderIds) => {
    const token = getAuthToken();
    if (!token || !workspace?.collaboration?.id) return;
    try {
      const res = await fetch(`${apiBase}/api/collab/deliverables/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          collaborationId: workspace.collaboration.id,
          itemIds: newOrderIds,
        }),
      });
      const data = await res.json();
      if (!data.ok) {
        console.error('Deliverable reorder failed:', data.error);
        fetchWorkspace();
      }
    } catch (err) {
      console.error('Failed to reorder deliverables:', err);
      fetchWorkspace();
    }
  }, [workspace?.collaboration?.id, apiBase, fetchWorkspace]);

  // Add deliverable
  const handleAddDeliverable = async () => {
    if (!newDeliverable.title.trim() && !newDeliverable.url.trim()) return;

    const token = getAuthToken();
    if (!token) return;

    setIsSubmitting(true);
    try {
      await fetch(`${apiBase}/api/collab/deliverables/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          collaborationId: workspace.collaboration.id,
          kind: newDeliverable.kind,
          title: newDeliverable.title,
          url: newDeliverable.url,
          description: newDeliverable.description,
        }),
      });
      
      setNewDeliverable({ kind: 'link', title: '', url: '', description: '' });
      setShowAddDeliverable(false);
      fetchWorkspace();
    } catch (err) {
      console.error('Failed to add deliverable:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle file upload for deliverables
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      const url = await uploadFile(file);
      setNewDeliverable(prev => ({
        ...prev,
        kind: 'file',
        title: prev.title || file.name,
        url,
      }));
    } catch (err) {
      console.error('Failed to upload file:', err);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploadingFile(false);
    }
  };

  // Update deliverable status
  const handleUpdateDeliverable = async (deliverableId, action, opts = {}) => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const { feedback, url } = opts || {};
      await fetch(`${apiBase}/api/collab/deliverables/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ deliverableId, action, feedback, url }),
      });
      
      fetchWorkspace();
    } catch (err) {
      console.error('Failed to update deliverable:', err);
    }
  };

  const _handleStartRequestChanges = (deliverableId) => {
    setRequestingChangesDeliverableId(deliverableId);
    setRequestChangesText('');
  };

  const _handleSubmitRequestChanges = async (deliverableId) => {
    const trimmed = requestChangesText.trim();
    if (!trimmed) {
      alert('Please add a short note describing what you want changed.');
      return;
    }
    await handleUpdateDeliverable(deliverableId, 'request_changes', { feedback: trimmed });
    setRequestingChangesDeliverableId(null);
    setRequestChangesText('');
  };

  const _handleStartResubmit = (deliverable) => {
    setResubmittingDeliverableId(deliverable.id);
    setResubmitNoteText('');
    setResubmitUrlText(deliverable.url || '');
  };

  const _handleSubmitResubmit = async (deliverable) => {
    const note = resubmitNoteText.trim();
    const urlToSend = resubmitUrlText.trim() || deliverable.url || '';
    if (!urlToSend) {
      alert('Please add the link/file URL before resubmitting.');
      return;
    }
    await handleUpdateDeliverable(deliverable.id, 'submit', { feedback: note || undefined, url: urlToSend });
    setResubmittingDeliverableId(null);
    setResubmitNoteText('');
    setResubmitUrlText('');
  };

  const _handleStartSubmit = (deliverable) => {
    setSubmittingDeliverableId(deliverable.id);
    setSubmitUrlText(deliverable.url || '');
    setSubmitNoteText('');
  };

  const _handleSubmitSubmit = async (deliverable) => {
    const note = submitNoteText.trim();
    const urlToSend = submitUrlText.trim() || deliverable.url || '';
    if (!urlToSend) {
      alert('Please add the link/file URL before submitting.');
      return;
    }
    await handleUpdateDeliverable(deliverable.id, 'submit', { feedback: note || undefined, url: urlToSend });
    setSubmittingDeliverableId(null);
    setSubmitUrlText('');
    setSubmitNoteText('');
  };

  const _handleSubmitUploadForDeliverable = async (event, which) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (which === 'submit') setSubmitUploading(true);
    if (which === 'resubmit') setResubmitUploading(true);

    try {
      const url = await uploadFile(file);
      if (which === 'submit') setSubmitUrlText(url);
      if (which === 'resubmit') setResubmitUrlText(url);
    } catch (err) {
      console.error('Failed to upload file:', err);
      alert('Failed to upload file. Please try again.');
    } finally {
      if (which === 'submit') setSubmitUploading(false);
      if (which === 'resubmit') setResubmitUploading(false);
    }
  };

  // Submit check-in
  const _handleSubmitCheckin = async (status) => {
    const token = getAuthToken();
    if (!token) return;

    try {
      await fetch(`${apiBase}/api/collab/checkin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          collaborationId: workspace.collaboration.id,
          status,
          note: checkinNote,
        }),
      });
      
      setShowCheckin(false);
      setCheckinNote('');
      fetchWorkspace();
    } catch (err) {
      console.error('Failed to submit check-in:', err);
    }
  };

  // Save approximate collaboration/posting dates to the collaboration record.
  const handleSaveApproxDates = async ({ force = false } = {}) => {
    const token = getAuthToken();
    if (!token || !workspace?.collaboration?.id) return false;

    const currentCollabDate = toYMD(workspace.collaboration.startDate);
    const currentPostingDate = toYMD(workspace.collaboration.endDate);
    const nextCollabDate = approxCollaborationDate || '';
    const nextPostingDate = approxPostingDate || '';
    const unchanged = currentCollabDate === nextCollabDate && currentPostingDate === nextPostingDate;
    if (unchanged && !force) return true;

    setSavingApproxDates(true);
    setApproxDateError('');
    setApproxDateSavedNotice(false);
    try {
      const response = await fetch(`${apiBase}/api/collab/update-dates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          collaborationId: workspace.collaboration.id,
          collaborationDate: nextCollabDate || null,
          postingDate: nextPostingDate || null,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.ok) {
        throw new Error(data.message || 'Could not save dates');
      }

      await fetchWorkspace();
      if (approxDateSavedNoticeTimeoutRef.current) {
        clearTimeout(approxDateSavedNoticeTimeoutRef.current);
      }
      setApproxDateSavedNotice(true);
      approxDateSavedNoticeTimeoutRef.current = setTimeout(() => {
        setApproxDateSavedNotice(false);
        approxDateSavedNoticeTimeoutRef.current = null;
      }, 2400);
      return true;
    } catch (err) {
      console.error('Failed to save approximate dates:', err);
      setApproxDateError(err.message || 'Could not save dates. Please try again.');
      setApproxDateSavedNotice(false);
      return false;
    } finally {
      setSavingApproxDates(false);
    }
  };

  const handleSaveGeneralNotes = async () => {
    const token = getAuthToken();
    if (!token || !workspace?.collaboration?.id) return false;

    const trimmed = generalNotes.trim();
    if (!trimmed) return true;

    setSavingNotes(true);
    setNotesError('');
    setNotesSavedNotice(false);
    try {
      const response = await fetch(`${apiBase}/api/collab/update-notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          collaborationId: workspace.collaboration.id,
          message: trimmed,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.ok) {
        throw new Error(data.message || 'Could not save note');
      }

      setGeneralNotes('');
      await fetchWorkspace();
      if (notesSavedNoticeTimeoutRef.current) {
        clearTimeout(notesSavedNoticeTimeoutRef.current);
      }
      setNotesSavedNotice(true);
      notesSavedNoticeTimeoutRef.current = setTimeout(() => {
        setNotesSavedNotice(false);
        notesSavedNoticeTimeoutRef.current = null;
      }, 2400);
      return true;
    } catch (err) {
      console.error('Failed to save general note:', err);
      setNotesError(err.message || 'Could not save note. Please try again.');
      setNotesSavedNotice(false);
      return false;
    } finally {
      setSavingNotes(false);
    }
  };

  const handleManualSaveAll = async () => {
    setSavingAll(true);
    try {
      const [datesOk, notesOk] = await Promise.all([
        handleSaveApproxDates({ force: true }),
        handleSaveGeneralNotes(),
      ]);
      if (datesOk && notesOk) {
        showToast('Changes saved', 'success');
      } else {
        showToast('Could not save changes. Please try again.', 'error');
      }
    } catch (_err) {
      showToast('Could not save changes. Please try again.', 'error');
    } finally {
      setSavingAll(false);
    }
  };

  const handleGeneratePdf = () => {
    if (!workspace) return;
    const { collaboration, participants, checklistFlat, deliverables, target } = workspace;
    const host = participants?.find(p => p.role === 'host');
    const creator = participants?.find(p => p.role === 'creator');
    const agreements = (checklistFlat || []).filter(item => item.section === 'Agreements' || !item.section);
    const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'TBD';

    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Collaboration Agreement - ${collaboration.title || 'Postaway'}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #1a1a2e; padding: 48px; line-height: 1.6; max-width: 800px; margin: 0 auto; }
  .logo { font-size: 28px; font-weight: 300; letter-spacing: 0.1em; color: #0f084b; margin-bottom: 4px; }
  .logo sup { font-size: 9px; vertical-align: super; font-weight: 700; }
  .tagline { font-size: 10px; letter-spacing: 0.25em; color: #0f084b; opacity: 0.7; margin-bottom: 32px; text-transform: uppercase; }
  h1 { font-size: 22px; font-weight: 600; margin-bottom: 6px; color: #0f084b; }
  .meta { font-size: 13px; color: #555; margin-bottom: 24px; }
  .parties { display: flex; gap: 40px; margin-bottom: 32px; padding: 16px 0; border-top: 1px solid #e0e0e0; border-bottom: 1px solid #e0e0e0; }
  .party { flex: 1; }
  .party-role { font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; color: #888; margin-bottom: 4px; }
  .party-name { font-size: 16px; font-weight: 600; }
  .party-handle { font-size: 13px; color: #666; }
  .section-title { font-size: 16px; font-weight: 600; margin: 28px 0 12px; color: #0f084b; border-bottom: 2px solid #0f084b; padding-bottom: 6px; }
  .agreement-row { display: flex; align-items: flex-start; gap: 10px; padding: 8px 0; border-bottom: 1px solid #f0f0f0; }
  .checkbox { width: 16px; height: 16px; border: 2px solid #ccc; border-radius: 3px; flex-shrink: 0; margin-top: 2px; display: flex; align-items: center; justify-content: center; font-size: 11px; }
  .checkbox.checked { border-color: #0f084b; background: #0f084b; color: #fff; }
  .agreement-text { font-size: 14px; flex: 1; }
  .agreement-note { font-size: 12px; color: #888; margin-top: 2px; }
  .status-badge { display: inline-block; font-size: 11px; padding: 2px 8px; border-radius: 10px; font-weight: 600; text-transform: uppercase; }
  .status-confirmed { background: #e8f5e9; color: #2e7d32; }
  .status-pending { background: #fff3e0; color: #e65100; }
  .deliverable-row { padding: 10px 0; border-bottom: 1px solid #f0f0f0; }
  .deliverable-title { font-size: 14px; font-weight: 600; }
  .deliverable-detail { font-size: 12px; color: #666; margin-top: 2px; }
  .dates-row { display: flex; gap: 40px; margin-bottom: 8px; }
  .date-item { font-size: 14px; }
  .date-label { font-weight: 600; color: #0f084b; }
  .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e0e0e0; font-size: 11px; color: #999; text-align: center; }
  .target-name { font-size: 14px; color: #555; margin-bottom: 16px; }
  @media print { body { padding: 24px; } }
</style></head><body>
<div class="logo">postaway<sup>TM</sup></div>
<div class="tagline">Search. Stay. Share.</div>
<h1>${collaboration.title || 'Collaboration Agreement'}</h1>
<div class="meta">Status: ${(collaboration.status || '').toUpperCase()} &nbsp;|&nbsp; Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
${target?.name ? `<div class="target-name">Property: ${target.name}${target.location_city ? `, ${target.location_city}` : ''}${target.location_state ? `, ${target.location_state}` : ''}</div>` : ''}
<div class="parties">
  <div class="party">
    <div class="party-role">Host</div>
    <div class="party-name">${host?.first_name || host?.owner_name || 'Host'}</div>
    ${host?.handle ? `<div class="party-handle">@${host.handle}</div>` : ''}
  </div>
  <div class="party">
    <div class="party-role">Creator</div>
    <div class="party-name">${creator?.first_name || 'Creator'}</div>
    ${creator?.handle ? `<div class="party-handle">@${creator.handle}</div>` : ''}
  </div>
</div>
<div class="section-title">Dates</div>
<div class="dates-row">
  <div class="date-item"><span class="date-label">Collaboration Date:</span> ${formatDate(collaboration.startDate)}</div>
  <div class="date-item"><span class="date-label">Posting Date:</span> ${formatDate(collaboration.endDate)}</div>
</div>
<div class="section-title">Expectations &amp; Agreements</div>
${agreements.length > 0 ? agreements.map((a, i) => {
      const confirmed = a.creator_confirmed_at && a.host_confirmed_at;
      return `<div class="agreement-row">
  <div class="checkbox${confirmed ? ' checked' : ''}">${confirmed ? '&#10003;' : ''}</div>
  <div>
    <div class="agreement-text">${i + 1}. ${a.title || ''}</div>
    ${a.description ? `<div class="agreement-note">${a.description}</div>` : ''}
    <span class="status-badge ${confirmed ? 'status-confirmed' : 'status-pending'}">${confirmed ? 'Confirmed' : 'Pending'}</span>
  </div>
</div>`;
    }).join('') : '<p style="color:#888;font-size:14px;padding:8px 0;">No expectations added yet.</p>'}
${(deliverables || []).length > 0 ? `<div class="section-title">Deliverables</div>
${deliverables.map(d => `<div class="deliverable-row">
  <div class="deliverable-title">${d.title || d.kind || 'Deliverable'}</div>
  ${d.description ? `<div class="deliverable-detail">${d.description}</div>` : ''}
  <div class="deliverable-detail">Status: ${(d.status || 'pending').replace('_', ' ')}</div>
  ${d.url ? `<div class="deliverable-detail">Link: ${d.url}</div>` : ''}
</div>`).join('')}` : ''}
${(() => { const notes = typeof collaboration.notesThread === 'string' ? JSON.parse(collaboration.notesThread || '[]') : (collaboration.notesThread || []); return notes.length > 0 ? `<div class="section-title">General Notes</div>
${notes.map(n => `<div style="padding:6px 0;border-bottom:1px solid #f0f0f0;font-size:13px;">
  <strong>${n.firstName || 'Note'}:</strong> ${n.message || ''}
</div>`).join('')}` : ''; })()}
<div class="footer">Generated from Postaway&trade; Collab Workspace &mdash; postaway.com</div>
</body></html>`;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 400);
    }
  };

  // Small confetti pop from a checkbox when checking an item
  const fireCheckboxConfetti = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (rect.left + rect.width / 2) / window.innerWidth;
    const y = (rect.top + rect.height / 2) / window.innerHeight;
    confetti({
      particleCount: 25,
      spread: 50,
      origin: { x, y },
      startVelocity: 18,
      gravity: 1.2,
      ticks: 80,
      scalar: 0.7,
    });
  };

  // Fire confetti burst from the Mark Complete button
  const fireConfetti = () => {
    const btn = completeBtnRef.current;
    if (!btn) {
      // Fallback: center of screen
      confetti({ particleCount: 150, spread: 80, origin: { y: 0.5 } });
      return;
    }
    const rect = btn.getBoundingClientRect();
    const x = (rect.left + rect.width / 2) / window.innerWidth;
    const y = (rect.top + rect.height / 2) / window.innerHeight;

    // Initial burst
    confetti({ particleCount: 80, spread: 70, origin: { x, y }, startVelocity: 30 });
    // Delayed follow-up for a richer effect
    setTimeout(() => {
      confetti({ particleCount: 50, spread: 100, origin: { x, y }, startVelocity: 20 });
    }, 150);
    setTimeout(() => {
      confetti({ particleCount: 30, spread: 120, origin: { x, y }, startVelocity: 15, decay: 0.92 });
    }, 300);
  };

  // Mark collab as complete (caller's side only)
  const handleComplete = async () => {
    const myRole = workspace?.currentUser?.role;
    const roleLabel = myRole === 'host' ? 'host' : 'creator';
    if (!window.confirm(`Are you sure you want to mark your part as complete? (as ${roleLabel})`)) return;

    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await fetch(`${apiBase}/api/collab/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ collaborationId: workspace.collaboration.id }),
      });
      
      const data = await response.json();
      if (!data.ok && data.error === 'agreements_not_complete') {
        // List exactly which items are unlocked and who still needs to
        // confirm each (PRD 6) — no more mystery blockers.
        let message = data.message || 'All expectations must be confirmed by both parties before completing.';
        if (Array.isArray(data.unlockedItems) && data.unlockedItems.length > 0) {
          const lines = data.unlockedItems.map(item => {
            const who = item.waitingOn === 'both'
              ? 'waiting on both of you'
              : `waiting on the ${item.waitingOn}`;
            return `• ${item.title} (${who})`;
          });
          message += `\n\nStill needs confirmation:\n${lines.join('\n')}`;
        }
        alert(message);
        return;
      }

      if (!data.ok && data.error === 'collab_not_started') {
        alert(data.message || 'This collaboration has not started yet. You can mark it complete once the collab dates arrive.');
        return;
      }

      if (data.ok && data.bothComplete) {
        fireConfetti();
      }
      
      fetchWorkspace();
    } catch (err) {
      console.error('Failed to complete collab:', err);
    }
  };

  // Submit review using the collab reviews endpoint (logs event)
  const handleAcceptReview = async (reviewId, accept, reviewType) => {
    const token = getAuthToken();
    if (!token) return;
    const endpoint = reviewType === 'host_review'
      ? `${apiBase}/api/reviews/accept-review`
      : `${apiBase}/api/reviews/accept-creator-review`;
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ review_id: reviewId, accept }),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        fetchWorkspace();
      } else {
        console.error('Accept review failed:', data);
        alert(data.error || 'Failed to update review');
      }
    } catch (err) {
      console.error('Accept review error:', err);
      alert('Something went wrong. Please try again.');
    }
  };

  const handleSubmitReview = async () => {
    if (!reviewForm.text.trim()) return;
    
    const token = getAuthToken();
    if (!token) return;

    setReviewSubmitting(true);
    try {
      let reviewType;
      let targetProfileId;

      if (isCreator) {
        const hostParticipant = workspace.participants.find(p => p.role === 'host');
        if (!hostParticipant?.host_profile_id) {
          alert('Unable to find host profile');
          setReviewSubmitting(false);
          return;
        }
        reviewType = 'host_review';
        targetProfileId = hostParticipant.host_profile_id;
      } else if (isHost) {
        const creatorParticipant = workspace.participants.find(p => p.role === 'creator');
        if (!creatorParticipant?.creator_profile_id) {
          alert('Unable to find creator profile');
          setReviewSubmitting(false);
          return;
        }
        reviewType = 'creator_review';
        targetProfileId = creatorParticipant.creator_profile_id;
      } else {
        alert('Unable to determine your role');
        setReviewSubmitting(false);
        return;
      }

      const response = await fetch(`${apiBase}/api/collab/reviews/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          collaborationId: workspace.collaboration.id,
          reviewType,
          targetProfileId,
          propertyId: workspace.collaboration.targetKind === 'property' ? workspace.collaboration.targetId : null,
          rating: reviewForm.rating,
          reviewTitle: reviewForm.title,
          reviewText: reviewForm.text,
          // Optional — never blocks the review. If an upload is still in
          // flight (or failed), the review goes out without that photo and the
          // user can add one later via Edit. Background = hero, circle =
          // small portrait; each defaults to a profile pic when null.
          heroImageUrl: reviewHero.url || null,
          circleImageUrl: reviewCircle.url || null,
          editReviewId: editingReviewId || undefined,
        }),
      });

      const data = await response.json();
      if (data.ok) {
        setReviewSubmitted(true);
        setReviewForm({ rating: 5, title: '', text: '' });
        setReviewHero(EMPTY_HERO);
        setReviewCircle(EMPTY_HERO);
        setEditingReviewId(null);
        fetchWorkspace();
      } else {
        alert('Failed to submit review: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Failed to submit review:', err);
      alert('Failed to submit review. Please try again.');
    } finally {
      setReviewSubmitting(false);
    }
  };

  // ── DnD hooks (must be before early returns) ──
  const agreementsRaw = workspace?.checklistFlat?.filter(item => item.section?.toLowerCase() === 'agreements') || [];
  const draggableAgreements = agreementsRaw.filter(item => !isMandatoryAgreement(item));
  const fixedAgreements = agreementsRaw.filter(item => isMandatoryAgreement(item));
  const agreements = [...draggableAgreements, ...fixedAgreements];
  const draggableAgreementIds = useMemo(() => draggableAgreements.map(a => a.id), [draggableAgreements]);

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const deliverableIds = useMemo(() => (workspace?.deliverables || []).map(d => d.id), [workspace?.deliverables]);

  const handleAgreementDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = draggableAgreements.findIndex(a => a.id === active.id);
    const newIndex = draggableAgreements.findIndex(a => a.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(draggableAgreements, oldIndex, newIndex);
    const newChecklistFlat = [
      ...(workspace?.checklistFlat || []).filter(i => i.section?.toLowerCase() !== 'agreements'),
      ...reordered,
      ...fixedAgreements,
    ];
    setWorkspace(prev => ({ ...prev, checklistFlat: newChecklistFlat }));
    handleReorderAgreements(reordered.map(a => a.id));
  }, [draggableAgreements, fixedAgreements, workspace?.checklistFlat, handleReorderAgreements]);

  const handleDeliverableDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const delivs = workspace?.deliverables || [];
    const oldIndex = delivs.findIndex(d => d.id === active.id);
    const newIndex = delivs.findIndex(d => d.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(delivs, oldIndex, newIndex);
    setWorkspace(prev => ({ ...prev, deliverables: reordered }));
    handleReorderDeliverables(reordered.map(d => d.id));
  }, [workspace?.deliverables, handleReorderDeliverables]);

  if (loading) {
    return (
      <div className={styles.workspace}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <span>Loading workspace...</span>
        </div>
      </div>
    );
  }

  if (error || !workspace) {
    return (
      <div className={styles.workspace}>
        <div className={styles.error}>
          <AlertCircle size={48} />
          <h3>Could not load workspace</h3>
          <p>{error || 'No active collaboration found'}</p>
        </div>
      </div>
    );
  }

  const { collaboration, participants, deliverables, checkins, progress, stepProgress, lastActivityAt, target } = workspace;
  const participantAvatarByUserId = new Map(
    participants.map((participant) => [
      participant.user_id,
      getOptimizedAvatarUrl(participant.avatar_url) || participant.avatar_url || DEFAULT_AVATAR,
    ])
  );
  const getParticipantAvatarSrc = (participant) =>
    getOptimizedAvatarUrl(participant?.avatar_url) || participant?.avatar_url || DEFAULT_AVATAR;
  const getHeroAvatarSrc = (participant) => {
    const url = participant?.hero_image_url || participant?.avatar_url;
    if (!url) return DEFAULT_AVATAR;
    return getCloudinaryUrl(url, { width: 450, height: 600, crop: 'fill', quality: 'auto', format: 'auto' }) || url;
  };
  const getActivityAvatarSrc = (event) =>
    getOptimizedAvatarUrl(event?.actor_avatar_url) ||
    participantAvatarByUserId.get(event?.actor_user_id) ||
    DEFAULT_AVATAR;
  const hostParticipant = participants.find(p => p.role === 'host');
  const creatorParticipant = participants.find(p => p.role === 'creator');
  const participantRoleByUserId = new Map(
    participants.map(p => [p.user_id, p.role])
  );

  // Build the data shape ReviewShareModal expects from a workspace review.
  // `type` is the reviewType: 'creator' = review about the creator (written
  // by the host); 'host' = review about the host (written by the creator).
  const buildShareReview = (rev, type) => {
    const subject = type === 'creator' ? creatorParticipant : hostParticipant;
    const reviewer = type === 'creator' ? hostParticipant : creatorParticipant;
    return {
      id: rev.id,
      reviewType: type,
      backgroundImageUrl: rev.heroImageUrl ?? null,
      circleImageUrl: rev.circleImageUrl ?? null,
      subjectAvatarUrl: subject?.avatar_url || null,
      reviewerAvatarUrl: reviewer?.avatar_url || null,
      reviewText: rev.text || '',
      rating: rev.rating,
      subjectName: subject?.first_name || (type === 'creator' ? 'Creator' : 'Host'),
      subjectHandle: subject?.handle || null,
      reviewerHandle: reviewer?.handle || null,
    };
  };

  const shareButtonStyle = {
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    fontSize: '12px', fontWeight: 600, color: '#0f084b',
    background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px',
  };

  const collabTargetLabel = target?.name
    ? target.name
    : collaboration.snapshot?.title || null;
  // Completion is gated on the collab having started: right after setup the
  // agreements are all locked, and an accidental "Mark complete" click here
  // used to arm the 14-day auto-complete months before the stay (the
  // Evergreen incident). The API enforces the same rule (collab_not_started).
  const collabStartYMD = toYMD(collaboration?.startDate);
  const collabNotStarted = !!collabStartYMD && collabStartYMD > toYMD(new Date());
  const collabStartLabel = collabNotStarted
    ? new Date(`${collabStartYMD}T00:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
    : null;
  const canMarkComplete = collaboration?.status === 'active' && progress?.allLocked && progress?.totalAgreements > 0 && !collabNotStarted;
  const approxDatesChanged =
    (approxCollaborationDate || '') !== toYMD(collaboration?.startDate) ||
    (approxPostingDate || '') !== toYMD(collaboration?.endDate);
  const showApproxDateSavedState = approxDateSavedNotice && !savingApproxDates && !approxDateError && !approxDatesChanged;
  const notesChanged = generalNotes.trim().length > 0;
  const showNotesSavedState = notesSavedNotice && !savingNotes && !notesError;

  // Get current user's confirmation state for an agreement item
  const _getUserConfirmation = (item) => {
    if (isCreator) {
      return !!item.creator_confirmed_at;
    } else if (isHost) {
      return !!item.host_confirmed_at;
    }
    return false;
  };

  // Get partner's confirmation state
  const _getPartnerConfirmation = (item) => {
    if (isCreator) {
      return !!item.host_confirmed_at;
    } else if (isHost) {
      return !!item.creator_confirmed_at;
    }
    return false;
  };

  // Check if item is locked (both confirmed)
  const isItemLocked = (item) => {
    return !!item.completed_at;
  };

  // ── Step progress helpers ────────────────────────────────────
  const getStepState = (stepId) => {
    if (!stepProgress?.steps?.[stepId]) return 'upcoming';
    return stepProgress.steps[stepId].complete ? 'completed' : 'upcoming';
  };

  const step1Complete = stepProgress?.steps?.agreements?.complete || false;

  // Activity summary for collapsed state
  const activityCount = (checkins?.length || 0) + (workspace.recentEvents?.length || 0);
  const latestActivityTime = workspace.recentEvents?.[0]?.created_at || checkins?.[0]?.created_at;

  // Keyboard handler for step navigation (Left/Right arrows)
  const handleStepKeyDown = (e, idx) => {
    let nextIdx = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      nextIdx = (idx + 1) % STEPS.length;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      nextIdx = (idx - 1 + STEPS.length) % STEPS.length;
    } else if (e.key === 'Home') {
      e.preventDefault();
      nextIdx = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      nextIdx = STEPS.length - 1;
    }
    if (nextIdx !== null) {
      setActiveTab(STEPS[nextIdx].id);
      stepRefs.current[nextIdx]?.focus();
    }
  };

  return (
    <div className={styles.workspace}>
      {/* Header - Compact */}
      <div className={styles.header}>
        <div className={styles.headerRow}>
          <div className={styles.headerLeft}>
            {_onClose && (
              <button className={styles.mobileBackBtn} onClick={_onClose} aria-label="Go back">
                <ArrowLeft size={20} />
              </button>
            )}
            <Briefcase size={20} />
            <h2>{collaboration.title || 'My Collab Tracker'}</h2>
            <span className={cn(styles.statusChip, styles[`status-${collaboration.status}`])}>
              {collaboration.status}
            </span>
            {collaboration.status === 'active' && lastActivityAt && (
              <CollabActivityTimer lastActivityAt={lastActivityAt} />
            )}
          </div>
          <CollabIntroVideo />
        </div>

        {/* Hero: Host × Creator */}
        <div className={styles.heroRow}>
          {hostParticipant && (
            <div className={styles.heroCard}>
              <div className={styles.heroAvatarWrap}>
                <img
                  src={getHeroAvatarSrc(hostParticipant)}
                  alt={hostParticipant.first_name || 'Host'}
                  className={styles.heroAvatar}
                />
              </div>
              <div className={styles.heroCardInfo}>
                <span className={styles.heroName}>{hostParticipant.first_name || 'Host'}</span>
                {hostParticipant.handle && (
                  <span className={styles.heroHandle}>@{hostParticipant.handle}</span>
                )}
                <span className={cn(styles.heroRole, styles.heroRoleHost)}>Host</span>
              </div>
            </div>
          )}

          <span className={styles.heroCross}>×</span>

          {creatorParticipant && (
            <div className={styles.heroCard}>
              <div className={styles.heroAvatarWrap}>
                <img
                  src={getHeroAvatarSrc(creatorParticipant)}
                  alt={creatorParticipant.first_name || 'Creator'}
                  className={styles.heroAvatar}
                />
              </div>
              <div className={styles.heroCardInfo}>
                <span className={styles.heroName}>{creatorParticipant.first_name || 'Creator'}</span>
                {creatorParticipant.handle && (
                  <span className={styles.heroHandle}>@{creatorParticipant.handle}</span>
                )}
                <span className={cn(styles.heroRole, styles.heroRoleCreator)}>Creator</span>
              </div>
            </div>
          )}
        </div>

        {/* Collabing on: property name (dropdown for hosts with 2+ properties) */}
        {(collabTargetLabel || (isHost && hostProperties.length > 1)) && (
          <div className={styles.heroTarget}>
            <span className={styles.heroTargetLabel}>Collabing with:</span>
            {isHost && hostProperties.length > 1 ? (
              <select
                className={styles.heroTargetSelect}
                value={collaboration.targetId || ''}
                onChange={(e) => handleChangeTargetProperty(e.target.value)}
                disabled={savingTarget}
              >
                {!collaboration.targetId && (
                  <option value="" disabled>Select a property</option>
                )}
                {hostProperties.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            ) : (
              <span className={styles.heroTargetName}>{collabTargetLabel}</span>
            )}
          </div>
        )}
      </div>

      {/* Workspace instructions — collapsible */}
      <details className={styles.workspaceInstructions} open>
        <summary className={styles.workspaceInstructionsSummary}>How This Collaboration Works</summary>
        <p>
          Congratulations on being accepted for a collaboration on Postaway! Below, please define expectations and deliverables for your collab. These terms are negotiable, so please add, delete or edit any of the suggested deliverables listed below to customize your collaboration. Both Host and Creator must agree on each deliverable by checking their respective boxes to lock the deliverable. Please note there are 3 steps to this collaboration: <strong>1) Defining Expectations</strong>, <strong>2) Uploading Content</strong> and <strong>3) Reviewing your experience</strong>. Thank you for following through on all three steps and subsequently marking the collab as complete.
        </p>
      </details>

      {/* ── 3-Step Progress Tracker ─────────────────────────── */}
      <div className={styles.stepTracker}>
        {/* Overall progress bar behind the steps */}
        <div
          className={styles.stepTrackerBar}
          role="progressbar"
          aria-valuenow={stepProgress?.percent || 0}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Collaboration progress: ${stepProgress?.completedCount || 0} of ${stepProgress?.totalCount || 3} steps complete`}
        >
          <div
            className={styles.stepTrackerFill}
            style={{ width: `${stepProgress?.percent || 0}%` }}
          />
        </div>

        {/* Step nav buttons */}
        <div className={styles.stepNav} role="tablist" aria-label="Collaboration steps">
          {STEPS.map((step, idx) => {
            const state = getStepState(step.id);
            const isActive = activeTab === step.id;
            const isCurrent = !isActive && stepProgress?.nextStepId === step.id;
            const StepIcon = step.Icon;
            return (
              <button
                key={step.id}
                ref={(el) => { stepRefs.current[idx] = el; }}
                role="tab"
                aria-selected={isActive}
                aria-label={`Step ${step.step}: ${step.label} – ${state === 'completed' ? 'complete' : 'incomplete'}`}
                tabIndex={isActive ? 0 : -1}
                className={cn(styles.stepItem, {
                  [styles.stepActive]: isActive,
                  [styles.stepCompleted]: state === 'completed',
                  [styles.stepCurrent]: isCurrent,
                })}
                onClick={() => setActiveTab(step.id)}
                onKeyDown={(e) => handleStepKeyDown(e, idx)}
              >
                <span className={styles.stepMarker}>
                  {state === 'completed' ? <Check size={14} /> : step.step}
                </span>
                <span className={styles.stepLabel}>
                  <StepIcon size={14} className={styles.stepLabelIcon} />
                  <span className={styles.stepLabelFull}>{step.label}</span>
                  <span className={styles.stepLabelMobile}>{step.mobileLabel}</span>
                </span>
                {idx < STEPS.length - 1 && <span className={styles.stepConnector} />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div className={styles.content}>
        {/* Agreements Tab */}
        {activeTab === 'agreements' && (
          <div className={styles.agreementsTab}>
            {/* Description */}
            <div className={styles.agreementsDescription}>
              <p>
                <strong>Define expectations and deliverables for your collaboration.</strong> Both host and creator must check off each item to confirm you agree. Once both checkboxes are checked, the item becomes locked.
              </p>
              <p className={styles.descriptionHint}>
                Tip: Click on any item to edit it. Use the pencil icon to suggest changes to your collaboration partner.
              </p>
            </div>

            {/* Sticky header */}
            <div className={styles.agreementsHeader}>
              <div className={styles.agreementsHeaderLabels}>
                <span className={styles.dragHandleHeaderSpacer} />
                <span className={cn(styles.checkboxLabel, styles.creatorLabel)}>Creator</span>
                <span className={cn(styles.checkboxLabel, styles.hostLabel)}>Host</span>
                <span className={styles.agreementLabel}>Expectation / Deliverable</span>
              </div>
            </div>

            {/* Scrollable list */}
            <div className={styles.agreementsListWrapper}>
              <div className={styles.agreementsList}>
              <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleAgreementDragEnd}>
              <SortableContext items={draggableAgreementIds} strategy={verticalListSortingStrategy}>
              {agreements.map(item => {
                const isLocked = isItemLocked(item);
                const isMandatory = isMandatoryAgreement(item);
                const isEditing = editingAgreementId === item.id && !isMandatory;
                const isSuggesting = suggestingId === item.id && !isMandatory;
                const creatorChecked = !!item.creator_confirmed_at;
                const hostChecked = !!item.host_confirmed_at;
                const hasPendingSuggestion = !!item.suggested_text;
                const isSuggestedByMe = item.suggested_by_user_id === workspace?.currentUser?.id;

                const rowContent = (sortableProps) => (
                  <div
                    className={cn(styles.agreementRow, {
                      [styles.locked]: isLocked,
                      [styles.editing]: isEditing,
                      [styles.hasSuggestion]: hasPendingSuggestion,
                      [styles.dragging]: sortableProps.isDragging,
                    })}
                    ref={sortableProps.setNodeRef}
                    style={sortableProps.style}
                    {...(sortableProps.attributes || {})}
                  >
                    {/* Drag handle */}
                    {!isMandatory && !isLocked && (
                      <div className={styles.dragHandleCell} {...(sortableProps.listeners || {})}>
                        <GripVertical size={16} className={styles.dragHandleIcon} />
                      </div>
                    )}
                    {(isMandatory || isLocked) && <div className={styles.dragHandlePlaceholder} />}

                    {/* Creator checkbox */}
                    <div className={styles.checkboxCell}>
                      <button
                        className={cn(styles.agreementCheckbox, styles.creatorCheckbox, { 
                          [styles.checked]: creatorChecked,
                          [styles.disabled]: !isCreator || isLocked || hasPendingSuggestion,
                        })}
                        onClick={(e) => {
                          if (isCreator && !isLocked && !hasPendingSuggestion) {
                            if (!creatorChecked) fireCheckboxConfetti(e);
                            handleToggleAgreement(item.id, !creatorChecked);
                          }
                        }}
                        disabled={!isCreator || isLocked || hasPendingSuggestion}
                      >
                        {creatorChecked && <Check size={12} />}
                      </button>
                    </div>

                    {/* Host checkbox */}
                    <div className={styles.checkboxCell}>
                      <button
                        className={cn(styles.agreementCheckbox, styles.hostCheckbox, { 
                          [styles.checked]: hostChecked,
                          [styles.disabled]: !isHost || isLocked || hasPendingSuggestion,
                        })}
                        onClick={(e) => {
                          if (isHost && !isLocked && !hasPendingSuggestion) {
                            if (!hostChecked) fireCheckboxConfetti(e);
                            handleToggleAgreement(item.id, !hostChecked);
                          }
                        }}
                        disabled={!isHost || isLocked || hasPendingSuggestion}
                      >
                        {hostChecked && <Check size={12} />}
                      </button>
                    </div>

                    {/* Agreement content */}
                    <div className={styles.agreementContent}>
                      {isLocked ? (
                        <div className={styles.lockedRow}>
                          {isMandatory && <span className={styles.requiredBadge}>Required</span>}
                          <span className={styles.lockedText}>{item.title}</span>
                          <div className={styles.lockedActions}>
                            <span className={styles.lockedIcon} title="Both parties confirmed">
                              <Lock size={14} />
                            </span>
                            <button 
                              className={styles.unlockBtn}
                              onClick={() => handleToggleAgreement(item.id, false)}
                              title="Unlock this item"
                            >
                              <LockOpen size={14} />
                              Unlock
                            </button>
                          </div>
                        </div>
                      ) : isSuggesting ? (
                        /* Suggesting mode - input to propose edit */
                        <div className={styles.suggestionForm}>
                          <div className={styles.suggestionFormHeader}>
                            <Pencil size={14} />
                            <span>Suggest a change</span>
                          </div>
                          <div className={styles.currentTextLabel}>
                            Current: <span>{item.title}</span>
                          </div>
                          <input
                            type="text"
                            value={suggestionText}
                            onChange={(e) => setSuggestionText(e.target.value)}
                            placeholder="Your suggested text..."
                            className={styles.suggestionInput}
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSubmitSuggestion(item.id);
                              if (e.key === 'Escape') handleCancelSuggest();
                            }}
                          />
                          <div className={styles.suggestionFormActions}>
                            <button 
                              className={styles.submitBtn}
                              onClick={() => handleSubmitSuggestion(item.id)}
                              disabled={!suggestionText.trim() || suggestionText === item.title}
                            >
                              Send Suggestion
                            </button>
                            <button 
                              className={styles.cancelBtn}
                              onClick={handleCancelSuggest}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : hasPendingSuggestion ? (
                        /* Pending suggestion - show both versions */
                        <div className={styles.suggestionPending}>
                          <div className={styles.conditionCurrent}>
                            <span className={styles.conditionLabel}>Current:</span>
                            <span className={styles.conditionText}>{item.title}</span>
                          </div>
                          <div className={styles.suggestionProposed}>
                            <span className={styles.suggestionLabel}>
                              <ArrowRight size={12} />
                              {isSuggestedByMe ? 'Your suggestion:' : 'Suggested change:'}
                            </span>
                            <span className={styles.suggestionText}>{item.suggested_text}</span>
                          </div>
                          {isSuggestedByMe ? (
                            <div className={styles.suggestionWaiting}>
                              <Clock size={14} />
                              Waiting for partner to respond...
                            </div>
                          ) : (
                            <div className={styles.suggestionActions}>
                              <button 
                                className={styles.acceptBtn}
                                onClick={() => handleAcceptSuggestion(item.id)}
                              >
                                <Check size={14} />
                                Accept
                              </button>
                              <button 
                                className={styles.declineBtn}
                                onClick={() => handleDeclineSuggestion(item.id)}
                              >
                                <X size={14} />
                                Decline
                              </button>
                              <button 
                                className={styles.counterBtn}
                                onClick={() => handleStartSuggest(item)}
                              >
                                <Pencil size={14} />
                                Counter
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        /* Normal editable mode */
                        <div className={styles.expectationWithNote}>
                          <div className={styles.inlineEditRow}>
                            {isMandatory && <span className={styles.requiredBadge}>Required</span>}
                            <textarea
                              rows={1}
                              value={isEditing ? editingAgreementText : item.title}
                              readOnly={isMandatory}
                              onChange={(e) => {
                                if (isMandatory) return;
                                if (!isEditing) {
                                  setEditingAgreementId(item.id);
                                  setEditingAgreementText(e.target.value);
                                } else {
                                  setEditingAgreementText(e.target.value);
                                }
                                // Auto-resize
                                e.target.style.height = 'auto';
                                e.target.style.height = e.target.scrollHeight + 'px';
                              }}
                              onFocus={(e) => {
                                if (isMandatory) return;
                                if (!isEditing) {
                                  setEditingAgreementId(item.id);
                                  setEditingAgreementText(item.title);
                                }
                                // Auto-resize on focus
                                e.target.style.height = 'auto';
                                e.target.style.height = e.target.scrollHeight + 'px';
                              }}
                              onBlur={() => {
                                // Save on blur if text changed
                                if (isEditing && editingAgreementText.trim() && editingAgreementText !== item.title) {
                                  handleSaveEditAgreement(item.id);
                                } else {
                                  handleCancelEditAgreement();
                                }
                              }}
                              className={styles.inlineInput}
                              ref={(el) => {
                                // Auto-resize on mount for long default text
                                if (el) {
                                  el.style.height = 'auto';
                                  el.style.height = el.scrollHeight + 'px';
                                }
                              }}
                              onKeyDown={(e) => {
                                if (isMandatory) return;
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  e.target.blur();
                                }
                                if (e.key === 'Escape') {
                                  handleCancelEditAgreement();
                                  e.target.blur();
                                }
                              }}
                            />
                            {!isMandatory && (
                              <Tippy content="View or edit notes" placement="auto" appendTo={() => document.body} touch={['hold', 500]}>
                                <button 
                                  className={cn(styles.noteBtn, { [styles.hasNote]: !!item.description })}
                                  onClick={() => handleToggleNote(item.id)}
                                  aria-label="View or edit notes"
                                >
                                  <FileText size={14} />
                                  {item.description && <span className={styles.noteDot} />}
                                </button>
                              </Tippy>
                            )}
                            {!isMandatory && (
                              <Tippy content="Suggest a change" placement="auto" appendTo={() => document.body} touch={['hold', 500]}>
                                <button 
                                  className={styles.suggestBtn}
                                  onClick={() => handleStartSuggest(item)}
                                  aria-label="Edit expectations or deliverables"
                                >
                                  <Pencil size={14} />
                                </button>
                              </Tippy>
                            )}
                            {!isMandatory && (
                              <button 
                                className={styles.deleteBtn}
                                onClick={() => handleDeleteAgreement(item.id, item.title)}
                                title="Delete"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                          
                          {/* Expandable notes section */}
                          {expandedNoteId === item.id && (
                            <div className={styles.noteSection}>
                              {editingNoteId === item.id ? (
                                <div className={styles.noteEditForm}>
                                  <textarea
                                    value={editingNoteText}
                                    onChange={(e) => setEditingNoteText(e.target.value)}
                                    placeholder="Add context or details about this expectation..."
                                    className={styles.noteTextarea}
                                    autoFocus
                                    rows={3}
                                  />
                                  <div className={styles.noteActions}>
                                    <button 
                                      className={styles.saveBtn}
                                      onClick={() => handleSaveNote(item.id)}
                                    >
                                      Save
                                    </button>
                                    <button 
                                      className={styles.cancelBtn}
                                      onClick={() => {
                                        setEditingNoteId(null);
                                        setEditingNoteText('');
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className={styles.noteDisplay}>
                                  {item.description ? (
                                    <>
                                      <p className={styles.noteText}>{item.description}</p>
                                      <button 
                                        className={styles.editNoteBtn}
                                        onClick={() => handleStartEditNote(item)}
                                      >
                                        <Pencil size={12} />
                                        Edit
                                      </button>
                                    </>
                                  ) : (
                                    <button 
                                      className={styles.addNoteBtn}
                                      onClick={() => handleStartEditNote(item)}
                                    >
                                      <Plus size={12} />
                                      Add notes to explain this expectation
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );

                if (isMandatory) {
                  return <React.Fragment key={item.id}>{rowContent({})}</React.Fragment>;
                }
                return <SortableAgreementRow key={item.id} id={item.id} isLocked={isLocked}>{(props) => rowContent(props)}</SortableAgreementRow>;
              })}
              </SortableContext>
              </DndContext>

                {agreements.length === 0 && (
                  <p className={styles.emptyText}>No expectations yet. Click below to add one.</p>
                )}

                {/* Add another condition button */}
                <button 
                  className={styles.addConditionBtn}
                  onClick={handleAddAgreement}
                  disabled={isSubmitting}
                >
                  <Plus size={16} />
                  <span>Add expectation or deliverable</span>
                </button>

                <div
                  className={cn(styles.approxDateSection, {
                    [styles.approxDateSectionSaved]: showApproxDateSavedState,
                  })}
                >
                  <div className={styles.approxDateHeader}>
                    <Calendar size={14} />
                    <span>Add approximate date of posting and collaboration</span>
                  </div>

                  <div className={styles.approxDateGrid}>
                    <label className={styles.approxDateField}>
                      <span>Approx collaboration date</span>
                      <input
                        type="date"
                        value={approxCollaborationDate}
                        onChange={(e) => {
                          setApproxCollaborationDate(e.target.value);
                          setApproxDateError('');
                          setApproxDateSavedNotice(false);
                        }}
                      />
                    </label>

                    <label className={styles.approxDateField}>
                      <span>Approx posting date</span>
                      <input
                        type="date"
                        value={approxPostingDate}
                        onChange={(e) => {
                          setApproxPostingDate(e.target.value);
                          setApproxDateError('');
                          setApproxDateSavedNotice(false);
                        }}
                      />
                    </label>
                  </div>

                  <div className={styles.approxDateActions}>
                    <span
                      className={cn(styles.approxDateStatus, {
                        [styles.approxDateStatusError]: !!approxDateError,
                        [styles.approxDateStatusSuccess]: showApproxDateSavedState,
                      })}
                    >
                      {showApproxDateSavedState && <CheckCircle size={13} className={styles.approxDateStatusIcon} />}
                      {approxDateError
                        ? approxDateError
                        : savingApproxDates
                          ? 'Saving dates...'
                          : showApproxDateSavedState
                            ? 'Dates saved'
                            : approxDatesChanged
                              ? 'Unsaved changes'
                              : 'Saved'}
                    </span>
                    <button
                      type="button"
                      className={cn(styles.approxDateSaveBtn, {
                        [styles.approxDateSaveBtnSaved]: showApproxDateSavedState,
                      })}
                      onClick={handleSaveApproxDates}
                      disabled={savingApproxDates || !approxDatesChanged}
                    >
                      {savingApproxDates ? 'Saving...' : showApproxDateSavedState ? 'Saved' : 'Save dates'}
                    </button>
                  </div>
                </div>

                <div
                  className={cn(styles.generalNotesSection, {
                    [styles.generalNotesSectionSaved]: showNotesSavedState,
                  })}
                >
                  <div className={styles.generalNotesHeader}>
                    <StickyNote size={14} />
                    <span>General notes</span>
                  </div>

                  {collaboration.notesThread && collaboration.notesThread.length > 0 && (
                    <div className={styles.notesThread}>
                      {collaboration.notesThread.map((entry, i) => {
                        const entryRole = entry.userId ? participantRoleByUserId.get(entry.userId) : null;
                        return (
                        <div
                          key={i}
                          className={cn(styles.notesThreadEntry, {
                            [styles.notesThreadEntryHost]: entryRole === 'host',
                            [styles.notesThreadEntryCreator]: entryRole === 'creator',
                          })}
                        >
                          <span className={styles.notesThreadAuthor}>
                            {entry.firstName || 'Someone'} said:
                          </span>
                          <p className={styles.notesThreadMessage}>{entry.message}</p>
                          {entry.createdAt && (
                            <span className={styles.notesThreadTime}>
                              {new Date(entry.createdAt).toLocaleString()}
                            </span>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}

                  <textarea
                    className={styles.generalNotesTextarea}
                    placeholder="Add a note..."
                    value={generalNotes}
                    onChange={(e) => {
                      setGeneralNotes(e.target.value);
                      setNotesError('');
                      setNotesSavedNotice(false);
                    }}
                    rows={2}
                  />

                  <div className={styles.generalNotesActions}>
                    <span
                      className={cn(styles.generalNotesStatus, {
                        [styles.generalNotesStatusError]: !!notesError,
                        [styles.generalNotesStatusSuccess]: showNotesSavedState,
                      })}
                    >
                      {showNotesSavedState && <CheckCircle size={13} className={styles.generalNotesStatusIcon} />}
                      {notesError || ''}
                    </span>
                    <button
                      type="button"
                      className={cn(styles.generalNotesSaveBtn, {
                        [styles.generalNotesSaveBtnSaved]: showNotesSavedState,
                      })}
                      onClick={handleSaveGeneralNotes}
                      disabled={savingNotes || !notesChanged}
                    >
                      {savingNotes ? 'Saving...' : showNotesSavedState ? 'Sent' : 'Add note'}
                    </button>
                  </div>
                </div>

                {/* Step 1 completion CTA */}
                {collaboration.status === 'active' && (
                  <div className={styles.stepCompleteCta}>
                    <div className={styles.stepCompleteInfo}>
                      <span className={styles.stepCompleteCount}>
                        {progress.lockedItems}/{progress.totalAgreements} locked
                      </span>
                      <span className={styles.stepCompleteHint}>
                        {step1Complete
                          ? 'All expectations confirmed — move on to Content!'
                          : 'Both parties must confirm every item to complete this step.'}
                      </span>
                    </div>
                    <button
                      className={cn(styles.stepCompleteBtn, { [styles.stepCompleteBtnReady]: step1Complete })}
                      disabled={!step1Complete}
                      onClick={() => {
                        if (step1Complete) setActiveTab('media');
                      }}
                    >
                      <ArrowRight size={16} />
                      {step1Complete ? 'Continue to Content' : 'Complete Step 1'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Content Library Tab */}
        {activeTab === 'media' && (
          <div className={styles.mediaTab}>
            {/* Compact hero section */}
            <div className={styles.mediaHero}>
              <div className={styles.mediaIconRow}>
                <div className={styles.mediaIconBox}>
                  <Image size={20} />
                </div>
                <div className={styles.mediaIconBox}>
                  <Video size={20} />
                </div>
                <div className={styles.mediaIconBox}>
                  <Camera size={20} />
                </div>
              </div>
              <div className={styles.mediaHeroText}>
                <h2 className={styles.mediaTitle}>Content Library</h2>
                <p className={styles.mediaSubtitle}>
                  Store and share photos, videos, and files from your collaboration
                </p>
              </div>
            </div>

            {/* Info cards */}
            <div className={styles.mediaInfoCards}>
              <div className={styles.mediaInfoCard}>
                <FolderOpen size={18} className={styles.mediaInfoIcon} />
                <div className={styles.mediaInfoContent}>
                  <h4>Keep Everything Organized</h4>
                  <p>Both you and your partner can access uploads anytime.</p>
                </div>
              </div>
              <div className={styles.mediaInfoCard}>
                <Upload size={18} className={styles.mediaInfoIcon} />
                <div className={styles.mediaInfoContent}>
                  <h4>Optional Uploads</h4>
                  <p>Upload photos, videos, or links when you're ready.</p>
                </div>
              </div>
            </div>

            {/* Upload section */}
            <div className={styles.mediaUploadSection}>
              <h3>Upload Content</h3>
              <div className={styles.mediaUploadArea}>
                {!showAddDeliverable ? (
                  <button 
                    className={styles.mediaUploadBtn}
                    onClick={() => setShowAddDeliverable(true)}
                  >
                    <Plus size={20} />
                    <span>Add Photos, Videos, or Links</span>
                  </button>
                ) : (
                  <div className={styles.mediaAddForm}>
                    <div className={styles.formRow}>
                      <label>Type</label>
                      <div className={styles.kindToggle}>
                        <button
                          className={cn({ [styles.active]: newDeliverable.kind === 'link' })}
                          onClick={() => setNewDeliverable(p => ({ ...p, kind: 'link' }))}
                        >
                          <LinkIcon size={14} />
                          Link
                        </button>
                        <button
                          className={cn({ [styles.active]: newDeliverable.kind === 'file' })}
                          onClick={() => setNewDeliverable(p => ({ ...p, kind: 'file' }))}
                        >
                          <Upload size={14} />
                          File
                        </button>
                      </div>
                    </div>
                    <div className={styles.formRow}>
                      <label>Title <span style={{ fontWeight: 400, color: 'var(--n4, #9ca3af)', fontSize: '13px' }}>(optional)</span></label>
                      <p style={{ margin: '0 0 6px', fontSize: '13px', color: 'var(--n4, #6b7280)', lineHeight: '1.4' }}>
                        Give this deliverable a name so both parties can easily identify it.
                      </p>
                      <input
                        type="text"
                        value={newDeliverable.title}
                        onChange={(e) => setNewDeliverable(p => ({ ...p, title: e.target.value }))}
                        placeholder="e.g., Property Photos, Instagram Reel"
                      />
                    </div>
                    {newDeliverable.kind === 'link' ? (
                      <div className={styles.formRow}>
                        <label>URL</label>
                        <p style={{ margin: '0 0 6px', fontSize: '13px', color: 'var(--n4, #6b7280)', lineHeight: '1.4' }}>
                          Paste the link to your content — Instagram post, Google Drive folder, Dropbox, etc.
                        </p>
                        <input
                          type="url"
                          value={newDeliverable.url}
                          onChange={(e) => setNewDeliverable(p => ({ ...p, url: e.target.value }))}
                          placeholder="https://..."
                        />
                      </div>
                    ) : (
                      <div className={styles.formRow}>
                        <label>File</label>
                        <div className={styles.fileUpload}>
                          <input
                            type="file"
                            onChange={handleFileUpload}
                            disabled={uploadingFile}
                          />
                          {uploadingFile && <span>Uploading...</span>}
                          {newDeliverable.url && !uploadingFile && (
                            <span className={styles.uploadedFile}>File uploaded</span>
                          )}
                        </div>
                      </div>
                    )}
                    <div className={styles.formActions}>
                      <button 
                        className={styles.submitBtn}
                        onClick={handleAddDeliverable}
                        disabled={isSubmitting || (!newDeliverable.title.trim() && !newDeliverable.url.trim())}
                      >
                        {isSubmitting ? 'Adding...' : 'Add to Library'}
                      </button>
                      <button 
                        className={styles.cancelBtn}
                        onClick={() => setShowAddDeliverable(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Content grid */}
            {deliverables.length > 0 && (
              <div className={styles.mediaGrid}>
                <h3>Uploaded Content</h3>
                <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDeliverableDragEnd}>
                <SortableContext items={deliverableIds} strategy={verticalListSortingStrategy}>
                <div className={styles.mediaItems}>
                  {deliverables.map(d => (
                    <SortableDeliverableRow key={d.id} id={d.id}>
                      {(sortableProps) => (
                        <div
                          className={cn(styles.mediaItem, { [styles.dragging]: sortableProps.isDragging })}
                          ref={sortableProps.setNodeRef}
                          style={sortableProps.style}
                          {...sortableProps.attributes}
                        >
                          <div className={styles.dragHandleCell} {...sortableProps.listeners}>
                            <GripVertical size={16} className={styles.dragHandleIcon} />
                          </div>
                          <div className={styles.mediaItemIcon}>
                            {d.kind === 'link' ? <LinkIcon size={24} /> : <FileText size={24} />}
                          </div>
                          <div className={styles.mediaItemContent}>
                            <span className={styles.mediaItemTitle}>{d.title}</span>
                            {d.created_by_first_name && (
                              <span className={styles.mediaItemMeta}>Added by {d.created_by_first_name}</span>
                            )}
                          </div>
                          {d.url && (
                            <a
                              className={styles.mediaItemView}
                              href={d.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Eye size={16} />
                              View
                            </a>
                          )}
                        </div>
                      )}
                    </SortableDeliverableRow>
                  ))}
                </div>
                </SortableContext>
                </DndContext>
              </div>
            )}

            {deliverables.length === 0 && !showAddDeliverable && (
              <div className={styles.mediaEmpty}>
                <FolderOpen size={48} className={styles.mediaEmptyIcon} />
                <p>No content uploaded yet</p>
                <span>Add photos, videos, or links to build your content library</span>
              </div>
            )}
          </div>
        )}

        {/* Review Tab */}
        {activeTab === 'review' && (
          <div className={styles.reviewTab}>
            {/* Review Status for both parties */}
            <div className={styles.reviewSection}>
              <h4 style={{ marginBottom: '12px' }}>Review Status</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Creator's review status */}
                {(() => {
                  const rev = workspace.existingReviews?.creatorReview;
                  if (!rev) {
                    return (
                      <div style={{ padding: '12px 16px', background: 'var(--n7, #f4f4f4)', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Clock size={16} style={{ color: '#9ca3af' }} />
                        <span style={{ color: 'var(--n4, #6b7280)' }}>Creator has not yet submitted a review</span>
                      </div>
                    );
                  }
                  const statusLabel = rev.isAccepted ? 'Accepted & Visible' : 'Submitted - Pending Acceptance';
                  const statusColor = rev.isAccepted ? '#10b981' : '#f59e0b';
                  const StatusIcon = rev.isAccepted ? CheckCircle : Clock;
                  const canAccept = isHost && !rev.isAccepted;
                  return (
                    <div style={{ padding: '12px 16px', background: 'var(--n7, #f4f4f4)', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                        <StatusIcon size={16} style={{ color: statusColor }} />
                        <strong>Creator&apos;s Review</strong>
                        <span style={{
                          marginLeft: 'auto', fontSize: '12px', fontWeight: 600,
                          padding: '2px 8px', borderRadius: '4px',
                          background: rev.isAccepted ? '#d1fae5' : '#fef3c7',
                          color: rev.isAccepted ? '#065f46' : '#92400e',
                        }}>{statusLabel}</span>
                        {rev.isAccepted && (
                          <button
                            type="button"
                            style={shareButtonStyle}
                            onClick={() => setShareReview(buildShareReview(rev, 'host'))}
                          >
                            <Send size={14} />
                            Share
                          </button>
                        )}
                      </div>
                      {rev.heroImageUrl && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '4px 0 8px' }}>
                          <img
                            src={getCloudinaryUrl(rev.heroImageUrl, { width: 144, height: 180, crop: 'fill', quality: 'auto', format: 'auto' }) || rev.heroImageUrl}
                            alt=""
                            style={{ width: 48, height: 60, borderRadius: 6, objectFit: 'cover', flexShrink: 0, border: '1px solid rgba(15, 8, 75, 0.12)' }}
                          />
                          <span style={{ fontSize: '11px', color: '#9ca3af', lineHeight: 1.4 }}>
                            This photo will appear on the shareable review card
                          </span>
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', color: '#f59e0b', fontSize: '14px' }}>
                        {'★'.repeat(rev.rating)}{'☆'.repeat(5 - rev.rating)}
                      </div>
                      {rev.title && (
                        <p style={{ margin: '2px 0', fontSize: '14px', fontWeight: 600, color: '#111827' }}>{rev.title}</p>
                      )}
                      {rev.text && (
                        <p style={{ margin: '4px 0 0', fontSize: '14px', color: 'var(--n4, #6b7280)', lineHeight: '1.5' }}>
                          &ldquo;{rev.text.substring(0, 250)}{rev.text.length > 250 ? '...' : ''}&rdquo;
                        </p>
                      )}
                      <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#9ca3af' }}>
                        Submitted {new Date(rev.createdAt).toLocaleDateString()}
                      </p>
                      {canAccept && (
                        <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                          <button
                            onClick={() => handleAcceptReview(rev.id, true, 'host_review')}
                            style={{
                              padding: '8px 20px', borderRadius: '8px', border: 'none',
                              background: '#10b981', color: '#fff', fontSize: '13px',
                              fontWeight: 600, cursor: 'pointer',
                            }}
                          >
                            Accept Review
                          </button>
                          <button
                            onClick={() => handleAcceptReview(rev.id, false, 'host_review')}
                            style={{
                              padding: '8px 20px', borderRadius: '8px',
                              border: '1px solid #d1d5db', background: '#fff',
                              color: '#6b7280', fontSize: '13px', fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            Decline
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Host's review status */}
                {(() => {
                  const rev = workspace.existingReviews?.hostReview;
                  if (!rev) {
                    return (
                      <div style={{ padding: '12px 16px', background: 'var(--n7, #f4f4f4)', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Clock size={16} style={{ color: '#9ca3af' }} />
                        <span style={{ color: 'var(--n4, #6b7280)' }}>Host has not yet submitted a review</span>
                      </div>
                    );
                  }
                  const statusLabel = rev.isAccepted ? 'Accepted & Visible' : 'Submitted - Pending Acceptance';
                  const statusColor = rev.isAccepted ? '#10b981' : '#f59e0b';
                  const StatusIcon = rev.isAccepted ? CheckCircle : Clock;
                  const canAccept = isCreator && !rev.isAccepted;
                  return (
                    <div style={{ padding: '12px 16px', background: 'var(--n7, #f4f4f4)', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                        <StatusIcon size={16} style={{ color: statusColor }} />
                        <strong>Host&apos;s Review</strong>
                        <span style={{
                          marginLeft: 'auto', fontSize: '12px', fontWeight: 600,
                          padding: '2px 8px', borderRadius: '4px',
                          background: rev.isAccepted ? '#d1fae5' : '#fef3c7',
                          color: rev.isAccepted ? '#065f46' : '#92400e',
                        }}>{statusLabel}</span>
                        {rev.isAccepted && (
                          <button
                            type="button"
                            style={shareButtonStyle}
                            onClick={() => setShareReview(buildShareReview(rev, 'creator'))}
                          >
                            <Send size={14} />
                            Share
                          </button>
                        )}
                      </div>
                      {rev.heroImageUrl && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '4px 0 8px' }}>
                          <img
                            src={getCloudinaryUrl(rev.heroImageUrl, { width: 144, height: 180, crop: 'fill', quality: 'auto', format: 'auto' }) || rev.heroImageUrl}
                            alt=""
                            style={{ width: 48, height: 60, borderRadius: 6, objectFit: 'cover', flexShrink: 0, border: '1px solid rgba(15, 8, 75, 0.12)' }}
                          />
                          <span style={{ fontSize: '11px', color: '#9ca3af', lineHeight: 1.4 }}>
                            This photo will appear on the shareable review card
                          </span>
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', color: '#f59e0b', fontSize: '14px' }}>
                        {'★'.repeat(rev.rating)}{'☆'.repeat(5 - rev.rating)}
                      </div>
                      {rev.title && (
                        <p style={{ margin: '2px 0', fontSize: '14px', fontWeight: 600, color: '#111827' }}>{rev.title}</p>
                      )}
                      {rev.text && (
                        <p style={{ margin: '4px 0 0', fontSize: '14px', color: 'var(--n4, #6b7280)', lineHeight: '1.5' }}>
                          &ldquo;{rev.text.substring(0, 250)}{rev.text.length > 250 ? '...' : ''}&rdquo;
                        </p>
                      )}
                      <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#9ca3af' }}>
                        Submitted {new Date(rev.createdAt).toLocaleDateString()}
                      </p>
                      {canAccept && (
                        <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                          <button
                            onClick={() => handleAcceptReview(rev.id, true, 'creator_review')}
                            style={{
                              padding: '8px 20px', borderRadius: '8px', border: 'none',
                              background: '#10b981', color: '#fff', fontSize: '13px',
                              fontWeight: 600, cursor: 'pointer',
                            }}
                          >
                            Accept Review
                          </button>
                          <button
                            onClick={() => handleAcceptReview(rev.id, false, 'creator_review')}
                            style={{
                              padding: '8px 20px', borderRadius: '8px',
                              border: '1px solid #d1d5db', background: '#fff',
                              color: '#6b7280', fontSize: '13px', fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            Decline
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Past reviews by the current user */}
            {(() => {
              const myPastReviews = isCreator
                ? (workspace.existingReviews?.allCreatorReviews || [])
                : (workspace.existingReviews?.allHostReviews || []);
              if (myPastReviews.length === 0) return null;
              return (
                <div className={styles.reviewSection}>
                  <h4 style={{ marginBottom: '12px' }}>Your Past Reviews</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {myPastReviews.map(rev => (
                      <div key={rev.id} style={{ padding: '14px 16px', background: 'var(--n7, #f4f4f4)', borderRadius: '10px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                          <div style={{ display: 'flex', gap: '2px' }}>
                            {[1,2,3,4,5].map(s => (
                              <Star key={s} size={14} style={{ color: s <= rev.rating ? '#f59e0b' : '#d1d5db', fill: s <= rev.rating ? '#f59e0b' : 'none' }} />
                            ))}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '12px', color: 'var(--n4, #6b7280)' }}>
                              {new Date(rev.createdAt).toLocaleDateString()}
                            </span>
                            <button
                              type="button"
                              style={shareButtonStyle}
                              onClick={() => setShareReview(buildShareReview(rev, isCreator ? 'host' : 'creator'))}
                            >
                              <Send size={14} />
                              Share
                            </button>
                            <button
                              style={{ fontSize: '12px', color: '#dc1d08', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: '2px 6px' }}
                              onClick={() => {
                                setEditingReviewId(rev.id);
                                setReviewForm({ rating: rev.rating, title: rev.title || '', text: rev.text });
                                // Load saved photos so they can be replaced/removed —
                                // this is also how old reviews retroactively get photos.
                                setReviewHero({ ...EMPTY_HERO, url: rev.heroImageUrl || null });
                                setReviewCircle({ ...EMPTY_HERO, url: rev.circleImageUrl || null });
                                setReviewSubmitted(false);
                              }}
                            >
                              Edit
                            </button>
                          </div>
                        </div>
                        {rev.title && <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>{rev.title}</div>}
                        <p style={{ fontSize: '14px', lineHeight: '1.5', margin: 0, color: 'var(--n3, #374151)' }}>{rev.text}</p>
                        <div style={{ marginTop: '6px', fontSize: '11px', color: 'var(--n4, #9ca3af)' }}>
                          {rev.isAccepted ? 'Accepted' : 'Pending acceptance'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Review form -- show for new review or editing */}
            <div className={styles.reviewSection}>
              {reviewSubmitted && !editingReviewId ? (
                <div className={styles.reviewSuccess}>
                  <CheckCircle size={24} />
                  <h4>Your review has been submitted</h4>
                  <p>It will be visible on the other party&apos;s profile once accepted. You can see the status above.</p>
                  <button
                    style={{ marginTop: '12px', padding: '8px 20px', borderRadius: '8px', border: '2px solid #0f084b', background: 'white', color: '#0f084b', fontWeight: 600, cursor: 'pointer', fontSize: '14px' }}
                    onClick={() => {
                      setReviewSubmitted(false);
                      setEditingReviewId(null);
                      setReviewForm({ rating: 5, title: '', text: '' });
                      setReviewHero(EMPTY_HERO);
                      setReviewCircle(EMPTY_HERO);
                    }}
                  >
                    Write Another Review
                  </button>
                </div>
              ) : (() => {
                // Live preview: in-progress form state + real handles/avatars
                // from workspace.participants.
                const previewSubject = isCreator ? hostParticipant : creatorParticipant;
                const previewReviewer = isCreator ? creatorParticipant : hostParticipant;
                // Local object URL immediately, swap to Cloudinary when done
                const liveBg = (reviewHero.uploading ? reviewHero.localUrl : (reviewHero.url || reviewHero.localUrl)) || null;
                const liveCircle = (reviewCircle.uploading ? reviewCircle.localUrl : (reviewCircle.url || reviewCircle.localUrl)) || null;
                const previewCardProps = {
                  backgroundImageUrl: liveBg,
                  circleImageUrl: liveCircle,
                  subjectAvatarUrl: previewSubject?.avatar_url || null,
                  reviewerAvatarUrl: previewReviewer?.avatar_url || null,
                  reviewText: previewText,
                  rating: reviewForm.rating,
                  subjectName: previewSubject?.first_name || (isCreator ? 'Host' : 'Creator'),
                  subjectHandle: previewSubject?.handle || null,
                  reviewerHandle: previewReviewer?.handle || null,
                  reviewType: isCreator ? 'host' : 'creator',
                };
                const previewCaption = 'This is what you and Postaway can share once your review is accepted.';
                return (
                  <>
                    <div className={styles.reviewHeader}>
                      <h4>
                        {editingReviewId
                          ? 'Edit your review'
                          : isCreator ? 'Review your host' : isHost ? 'Review the creator' : 'Leave a review'}
                      </h4>
                      <p className={styles.reviewHint}>
                        {editingReviewId ? 'Update your review below' : 'Share your experience working together'}
                      </p>
                    </div>

                    <div className={styles.reviewFormLayout}>
                      <div className={styles.reviewForm}>
                        <div className={styles.formRow}>
                          <label>Rating</label>
                          <div className={styles.ratingStars}>
                            {[1, 2, 3, 4, 5].map(star => (
                              <button
                                key={star}
                                className={cn(styles.starBtn, { [styles.active]: reviewForm.rating >= star })}
                                onClick={() => setReviewForm(r => ({ ...r, rating: star }))}
                              >
                                <Star size={24} />
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className={styles.formRow}>
                          <label>Background photo (optional)</label>
                          <ReviewHeroUpload
                            hero={reviewHero}
                            onHeroChange={setReviewHero}
                            role={isCreator ? 'creator' : isHost ? 'host' : undefined}
                          />
                        </div>

                        <div className={styles.formRow}>
                          <label>Circle photo (optional)</label>
                          <ReviewHeroUpload
                            hero={reviewCircle}
                            onHeroChange={setReviewCircle}
                            label="Add a photo for the circle"
                            helper={`The small circle on the card. Defaults to ${previewSubject?.handle ? `@${previewSubject.handle}` : 'their'} profile photo (who the review is about).`}
                            uploadedNote="This photo will appear in the circle on your shareable card."
                          />
                        </div>

                        <div className={styles.formRow}>
                          <label>Title (optional)</label>
                          <input
                            type="text"
                            value={reviewForm.title}
                            onChange={(e) => setReviewForm(r => ({ ...r, title: e.target.value }))}
                            placeholder="Summarize your experience"
                          />
                        </div>

                        <div className={styles.formRow}>
                          <label>Your review</label>
                          <textarea
                            value={reviewForm.text}
                            onChange={(e) => setReviewForm(r => ({ ...r, text: e.target.value }))}
                            placeholder="Tell others about your experience..."
                            rows={4}
                          />
                        </div>

                        <button
                          type="button"
                          className={styles.previewMobileBtn}
                          onClick={() => setMobilePreviewOpen(true)}
                        >
                          <Eye size={16} />
                          Preview your card
                        </button>

                        <div className={styles.formActions}>
                          {editingReviewId && (
                            <button
                              className={styles.submitBtn}
                              style={{ background: 'transparent', color: 'var(--n3, #374151)', border: '1px solid var(--n5, #d1d5db)' }}
                              onClick={() => {
                                setEditingReviewId(null);
                                setReviewForm({ rating: 5, title: '', text: '' });
                                setReviewHero(EMPTY_HERO);
                                setReviewCircle(EMPTY_HERO);
                              }}
                            >
                              Cancel
                            </button>
                          )}
                          <button
                            className={styles.submitBtn}
                            onClick={handleSubmitReview}
                            disabled={reviewSubmitting || !reviewForm.text.trim()}
                          >
                            {reviewSubmitting ? 'Submitting...' : editingReviewId ? 'Update Review' : 'Submit Review'}
                          </button>
                        </div>
                      </div>

                      <aside className={styles.reviewPreviewCol}>
                        <div className={styles.reviewPreviewSticky}>
                          <h5 className={styles.reviewPreviewHeading}>Your shareable review card</h5>
                          <div className={styles.reviewPreviewCard}>
                            <ReviewShareCard {...previewCardProps} />
                          </div>
                          <p className={styles.reviewPreviewCaption}>{previewCaption}</p>
                        </div>
                      </aside>
                    </div>

                    {mobilePreviewOpen && (
                      <div
                        className={styles.previewSheet}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Preview your shareable review card"
                        onClick={() => setMobilePreviewOpen(false)}
                      >
                        <div className={styles.previewSheetInner} onClick={(e) => e.stopPropagation()}>
                          <h5 className={styles.reviewPreviewHeading}>Your shareable review card</h5>
                          <div className={styles.previewSheetCard}>
                            <ReviewShareCard {...previewCardProps} />
                          </div>
                          <p className={styles.reviewPreviewCaption}>{previewCaption}</p>
                          <button
                            type="button"
                            className={styles.previewSheetClose}
                            onClick={() => setMobilePreviewOpen(false)}
                          >
                            Keep editing
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Final collaboration completion */}
            {collaboration.status === 'active' && (
              <div className={styles.collabCompleteCta}>
                {/* One-sided completion banner (PRD 6): the other party has
                    confirmed — make finishing one click, and be upfront about
                    the 14-day auto-complete. Good news, not a warning. */}
                {((isHost && !collaboration.hostCompletedAt && collaboration.creatorCompletedAt) ||
                  (isCreator && !collaboration.creatorCompletedAt && collaboration.hostCompletedAt)) && (() => {
                  const partnerConfirmedAt = isHost
                    ? collaboration.creatorCompletedAt
                    : collaboration.hostCompletedAt;
                  const autoCompleteDate = partnerConfirmedAt
                    ? new Date(new Date(partnerConfirmedAt).getTime() + 14 * 24 * 60 * 60 * 1000)
                        .toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
                    : null;
                  return (
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      flexWrap: 'wrap', gap: '12px', padding: '14px 16px', marginBottom: '12px',
                      background: '#0f084b', color: '#ffffff', borderRadius: '12px',
                    }}>
                      <div style={{ fontSize: '14px', lineHeight: 1.5 }}>
                        <strong>Your partner marked this collab as complete.</strong>
                        <div style={{ fontSize: '13px', opacity: 0.85 }}>
                          Confirm to make it official{autoCompleteDate ? ` — auto-completes ${autoCompleteDate}` : ''}.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => { if (canMarkComplete) handleComplete(); }}
                        disabled={!canMarkComplete}
                        title={canMarkComplete
                          ? 'Confirm completion'
                          : collabNotStarted
                            ? `Your collab hasn't started yet — completion opens ${collabStartLabel}.`
                            : 'All expectations must be confirmed by both parties first.'}
                        style={{
                          padding: '9px 20px', borderRadius: '100px', border: '1.5px solid #ffffff',
                          background: 'transparent', color: '#ffffff', fontSize: '13.5px', fontWeight: 600,
                          cursor: canMarkComplete ? 'pointer' : 'default',
                          opacity: canMarkComplete ? 1 : 0.6, whiteSpace: 'nowrap',
                        }}
                      >
                        Confirm completion
                      </button>
                    </div>
                  );
                })()}
                <div className={styles.collabCompleteInfo}>
                  <CircleCheck size={20} />
                  <div>
                    <strong>Ready to wrap up?</strong>
                    <span>
                      {canMarkComplete
                        ? 'All expectations are confirmed. Mark your part as complete below.'
                        : collabNotStarted
                          ? `Your collab starts ${collabStartLabel}. You can mark it complete once it's underway.`
                          : 'All expectations must be confirmed by both parties before completing.'}
                    </span>
                  </div>
                </div>

                {/* Per-party completion status */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', margin: '8px 0' }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '6px 12px', borderRadius: '6px', fontSize: '13px',
                    background: collaboration.hostCompletedAt ? '#d1fae5' : '#f3f4f6',
                    color: collaboration.hostCompletedAt ? '#065f46' : '#6b7280',
                  }}>
                    {collaboration.hostCompletedAt ? <CheckCircle size={14} /> : <Clock size={14} />}
                    Host: {collaboration.hostCompletedAt ? 'Complete' : 'Pending'}
                  </div>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '6px 12px', borderRadius: '6px', fontSize: '13px',
                    background: collaboration.creatorCompletedAt ? '#d1fae5' : '#f3f4f6',
                    color: collaboration.creatorCompletedAt ? '#065f46' : '#6b7280',
                  }}>
                    {collaboration.creatorCompletedAt ? <CheckCircle size={14} /> : <Clock size={14} />}
                    Creator: {collaboration.creatorCompletedAt ? 'Complete' : 'Pending'}
                  </div>
                </div>

                {/* Show the button only if this user hasn't already marked complete */}
                {!(isHost && collaboration.hostCompletedAt) && !(isCreator && collaboration.creatorCompletedAt) && (
                  <button
                    ref={completeBtnRef}
                    className={cn(styles.completeBtn, { [styles.completeBtnReady]: canMarkComplete })}
                    onClick={() => { if (canMarkComplete) handleComplete(); }}
                    disabled={!canMarkComplete}
                  >
                    <CircleCheck size={16} />
                    Mark My Part Complete
                  </button>
                )}

                {/* Show confirmation if this user already marked complete */}
                {((isHost && collaboration.hostCompletedAt) || (isCreator && collaboration.creatorCompletedAt)) && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '8px 12px', borderRadius: '6px',
                    background: '#d1fae5', color: '#065f46', fontSize: '14px',
                  }}>
                    <CheckCircle size={16} />
                    You've marked your part as complete
                  </div>
                )}
              </div>
            )}
          </div>
        )}

      </div>

      {/* ── Collapsible Activity Panel ──────────────────────── */}
      <div className={styles.activityAccordion}>
        <button
          className={cn(styles.activityAccordionToggle, { [styles.activityAccordionOpen]: activityOpen })}
          onClick={() => setActivityOpen(o => !o)}
          aria-expanded={activityOpen}
          aria-controls="collab-activity-panel"
        >
          <History size={16} />
          <span className={styles.activityAccordionTitle}>Activity</span>
          {!activityOpen && activityCount > 0 && (
            <span className={styles.activityAccordionMeta}>
              {activityCount} event{activityCount !== 1 ? 's' : ''}
              {latestActivityTime && (
                <> &middot; latest {new Date(latestActivityTime).toLocaleDateString()}</>
              )}
            </span>
          )}
          <ChevronDown
            size={16}
            className={cn(styles.activityAccordionChevron, { [styles.activityAccordionChevronOpen]: activityOpen })}
          />
        </button>

        <div
          id="collab-activity-panel"
          role="region"
          aria-label="Collaboration activity"
          className={cn(styles.activityAccordionBody, { [styles.activityAccordionBodyOpen]: activityOpen })}
        >
          <div className={styles.activityAccordionInner}>
            {/* Recent check-ins */}
            {checkins.length > 0 && (
              <div className={styles.checkinsSection}>
                <h4>Recent Check-ins</h4>
                <div className={styles.checkinsList}>
                  {checkins.slice(0, 5).map(c => (
                    <div key={c.id} className={styles.checkinItem}>
                      <span className={cn(styles.checkinStatus, styles[c.status])}>
                        {CHECKIN_OPTIONS.find(o => o.id === c.status)?.label || c.status}
                      </span>
                      {c.note && <p className={styles.checkinItemNote}>{c.note}</p>}
                      <span className={styles.checkinTime}>
                        {new Date(c.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Activity feed */}
            {workspace.recentEvents && workspace.recentEvents.length > 0 && (
              <div className={styles.activitySection}>
                <h4>Activity</h4>
                <div className={styles.activityList}>
                  {workspace.recentEvents.slice(0, 15).map(event => (
                    <div key={event.id} className={styles.activityItem}>
                      <img
                        src={getActivityAvatarSrc(event)}
                        alt=""
                        className={styles.activityAvatar}
                      />
                      <div className={styles.activityContent}>
                        <span className={styles.activityText}>
                          <strong>{event.actor_first_name || event.actor_handle || 'Someone'}</strong>
                          {' '}
                          {EVENT_LABELS[event.event_type] || event.event_type.replace(/_/g, ' ')}
                          {event.metadata?.title && (
                            <span className={styles.activityTarget}>: {event.metadata.title}</span>
                          )}
                        </span>
                        <span className={styles.activityTime}>
                          {new Date(event.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(!checkins.length && (!workspace.recentEvents || !workspace.recentEvents.length)) && (
              <p className={styles.emptyText}>No activity yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Manual Save Changes */}
      <div className={styles.saveChangesBar}>
        <button
          className={styles.pdfBtn}
          onClick={handleGeneratePdf}
        >
          <Printer size={16} />
          Generate Collab PDF
        </button>
        <button
          className={styles.saveChangesBtn}
          onClick={handleManualSaveAll}
          disabled={savingAll}
        >
          {savingAll ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <ReviewShareModal
        isOpen={!!shareReview}
        onClose={() => setShareReview(null)}
        review={shareReview}
        canEdit
        onImagesUpdated={() => fetchWorkspace()}
      />

    </div>
  );
};

export default CollabWorkspace;
